import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import DropdownMenu from "../../components/DropdownMenu";
import SEO from "../../context/SEO";
import { ShopContext } from "../../context/ShopContext";
import BathNuspa from "../../assets/project/Nuspa_02.avif";
import LushLeaves from "../../assets/project/lush_leaves.jpg";
import HandGranules from "../../assets/project/hands_cork_granules.jpg";
import Desk from "../../assets/project/desk.jpg";
import YourProject from "../../components/YourProject";
import BoxesMulti from "../../assets/products/boxes-multi.avif";
import styles from "./style.module.scss";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

// Register the plugin once
gsap.registerPlugin(ScrollToPlugin);

function Project() {
  const [showElements, setShowElements] = useState("full-opacity"); // ← Start visible
  const [visible, setVisible] = useState(false); // For first-load logo animation
  const [vh, setVh] = useState(window.innerHeight * 0.01);

  const prevScrollPos = useRef(window.scrollY);
  const scrollInitiated = useRef(false);
  const location = useLocation();

  const { products } = useContext(ShopContext);

  // Update vh on resize/orientation change
  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight * 0.01);
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  // First-load intro animation (logo appears → fades out after 6s)
  useEffect(() => {
    const timer1 = setTimeout(() => setVisible(true), 2000);
    const timer2 = setTimeout(() => setVisible(false), 6000);
    scrollInitiated.current = true; // Prevent re-trigger on scroll
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // MAIN SCROLL HANDLER — Same perfect logic as AllProducts
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollingUp = currentScrollPos < prevScrollPos.current;
      const scrollingDown = currentScrollPos > prevScrollPos.current;
      const atTop = currentScrollPos < 10 * vh;

      if (atTop) {
        setShowElements("full-opacity"); // ← No animation at top → smooth!
      } else if (scrollingUp) {
        setShowElements("fade-in-up"); // Show when scrolling up
      } else if (scrollingDown) {
        setShowElements("fade-out-up"); // Hide when scrolling down
      }

      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [vh]);

  // Smooth scroll to hash (e.g. #proposal)
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.slice(1);
      const elem = document.getElementById(hash);
      if (elem) {
        gsap.to(window, {
          delay: 0.5,
          duration: 1.4,
          scrollTo: { y: elem, offsetY: 80 },
          ease: "power2.inOut",
        });
      }
    }
  }, [location]);

  // Filter products
  const OrbL = products.filter((p) => p.name.includes("Orb L"));
  const PlaceMat = products.filter((p) => p.name.includes("Bowl L"));
  const Cookie = products.filter((p) => p.name.includes("Cookie"));

  return (
    <div>
      <DropdownMenu
        overideVisible={visible}
        showElements={showElements}
        iconColour="white"
      />
      <SEO title="Cork Matters - Your Project" />
      <div className={styles.banner}>
        <img src={BathNuspa} alt="Bathroom Nuspa" />
        <div className={styles.bannerText}>YOUR CORK PROJECT</div>
      </div>
      <div className={styles.projectSection}>
        <div className={styles.grid}>
          <div className={styles.textSection}>
            <div className={styles.title}>Your Cork Project</div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                Have an idea for a cork product and want to produce it? We can
                help you! We are located close to the cork product processing
                industries to advise you on the best production for your idea /
                collection made from Portuguese cork.
              </div>
              <div className={styles.mainText}>
                Since 2008, we have specialized in the development, production
                and prototyping of made-to-measure products, meeting the
                specific needs of each client. Our method is simple. If you
                already have your project designed, just send us your project
                plan, 3D images or any sketches you have to help us visualize
                your idea. We will then analyze all the information and send you
                a quote for the prototyping and production of a certain number
                of units. We usually need 5 to 10 working days to send you a
                quote. Once you approve it, we can proceed with production
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/project#proposal"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  Get in contact with us
                </Link>
              </div>
            </div>
          </div>
          <div className={styles.image}>
            <img src={LushLeaves} />
          </div>
        </div>
      </div>
      <div className={styles.banner}>
        <img src={HandGranules} alt="Hands immersed in cork granules" />
      </div>
      <div className={styles.projectSection}>
        <div className={styles.grid}>
          <div className={styles.imageLeft}>
            <img src={Desk} />
          </div>
          <div className={styles.textSectionRight}>
            <div className={styles.topContent}>
              <div className={styles.mainText}>
                In addition to cork, we often receive other items for assemblage
                with cork that need to be sent to Cork Matters so that the
                prototypes / products meet the tolerances required for the
                production of the products controlled by us (quality
                department). We can also produce new solutions for your project.
                We also take care of all the logistics details from our premises
                to the final destination. Before proceeding, please consult our
                <span className={styles.noahBold}>
                  {" "}
                  Terms and Conditions
                </span>{" "}
                link. Fill in the form below and we'll get back to you with all
                the information.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/termsandconditions"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  Go to Terms and Conditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="proposal">
        <YourProject />
      </div>
      <div className={styles.banner}>
        <img src={BoxesMulti} alt="Assorted Cork Boxes in different colours" />
      </div>
      <div className={styles.projectSection}>
        <div className={styles.grid}>
          <div className={styles.imageLeft}>
            {OrbL.map((product) => (
              <div className={styles.imageContainer}>
                <Link
                  to={`/product/${product.id}`}
                  className={styles.productLinkHome}
                >
                  <img src={product.variations[0].imageSrc[0]} />
                  <p className={styles.productTitleHome}>
                    {product.family
                      ? product.family.toUpperCase()
                      : product.name.toUpperCase()}{" "}
                    {product.family ? product.size.toUpperCase() : ""}{" "}
                  </p>
                </Link>
              </div>
            ))}
          </div>
          <div className={styles.textSectionRight}>
            <div className={styles.title}>Our Designers</div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                Carlos and Alzira have been working with cork for over twenty
                years. They have a passion for the material and a deep
                understanding of its properties and potential. They focus on
                shape, feel and form over unecessary complexity. They are
                inspired by the natural world and the beauty in simiplicity.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the Cork Matters shop
                </Link>
              </div>
            </div>
          </div>
          {PlaceMat.map((product) => (
            <div className={styles.image}>
              <div className={styles.imageContainer}>
                <Link
                  to={`/product/${product.id}`}
                  className={styles.productLinkHome}
                >
                  <img
                    src={product.variations[0].imageSrc[0]}
                    alt="Cork Trees"
                  />
                  <p className={styles.productTitleHome}>
                    {product.family
                      ? product.family.toUpperCase()
                      : product.name.toUpperCase()}{" "}
                    {product.family ? product.size.toUpperCase() : ""}{" "}
                  </p>
                </Link>
              </div>
            </div>
          ))}
          {Cookie.map((product) => (
            <div className={styles.image}>
              <div className={styles.imageContainer}>
                <Link
                  to={`/product/${product.id}`}
                  className={styles.productLinkHome}
                >
                  <img
                    src={product.variations[0].imageSrc[0]}
                    alt="Cork Trees"
                  />
                  <p className={styles.productTitleHome}>
                    {product.family
                      ? product.family.toUpperCase()
                      : product.name.toUpperCase()}{" "}
                    {product.family ? product.size.toUpperCase() : ""}{" "}
                  </p>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
export default Project;
